import React, { useCallback, useEffect, useState } from "react";
import { Box, Button, TextField, Typography } from "@mui/material";
import { useNavigate } from "react-router-dom";
import { useIsDesktop } from "../shared/hooks/useIsDesktop";
import ActionButton from "../shared/ActionButton";
import {
  BORDER_COLOR_LIGHT,
  SEND_COLOR,
  SEND_COLOR_DARK,
  TEXT_COLOR_PRIMARY,
} from "../../constants/colors";
import { toast } from "react-toastify";
import FrameCard from "../shared/FrameCard";
import { handleUnauthorizedOrRedirect } from "../utils/responseHandler";
import RequestStatusSwitch from "../shared/request/RequestStatusSwitch";
import ConfirmStopModal from "../shared/ConfirmStopModal";
import useRequestStatus from "../user/shared/hooks/useRequestStatus";
import ProfileVisibilitySection from "../shared/request/ProfileVisibilitySection";
import NoticeBanner from "../shared/NoticeBanner";
import MailSettingsSection from "./MailSettingsSection";
import { MAIL_MAGAZINE_OPTIONS } from "../../constants/mailMagazineOptions";

const defaultMailMagazineStatus = {
  subscribed_to_project: false,
  subscribed_to_useful_info: false,
  subscribed_to_notice: false,
};

// APIレスポンスからメール配信設定のみ取得
const pickMailMagazineStatus = (data) => ({
  subscribed_to_project:
    data?.subscribed_to_project ??
    defaultMailMagazineStatus.subscribed_to_project,
  subscribed_to_useful_info:
    data?.subscribed_to_useful_info ??
    defaultMailMagazineStatus.subscribed_to_useful_info,
  subscribed_to_notice:
    data?.subscribed_to_notice ??
    defaultMailMagazineStatus.subscribed_to_notice,
});

export default function UserSettings({ isSkillSheetBasicCompleted }) {
  const isDesktop = useIsDesktop();
  const navigate = useNavigate();

  const [displayedEmailAddress, setDisplayedEmailAddress] =
    useState("メールアドレス未登録");
  const [isLoading, setIsLoading] = useState(true);
  const [email, setEmail] = useState("");
  const [passwordChangeAllowed, setPasswordChangeAllowed] = useState(null);
  const [minimumPasswordLength, setMinimumPasswordLength] = useState(null);
  const [profilePublicFlag, setProfilePublicFlag] = useState(false);
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [newPasswordConfirmation, setNewPasswordConfirmation] = useState("");
  const [isSubmittingEmail, setIsSubmittingEmail] = useState(false);
  const [isSubmittingPassword, setIsSubmittingPassword] = useState(false);
  const [emailRegistered, setEmailRegistered] = useState(false);
  const [mailMagazineStatus, setMailMagazineStatus] = useState(
    defaultMailMagazineStatus,
  );
  const [isSubmittingMailMagazine, setIsSubmittingMailMagazine] =
    useState(false);

  const requestStatusErrorMessage =
    "読み込みに失敗しました。再読み込みしてください。";

  // セッション切れ
  const handleSessionExpired = useCallback((response) => {
    const sessionExpiredMessage =
      "セッションが切れました。ログインし直してください。";

    return handleUnauthorizedOrRedirect(response, {
      fallbackPath: "/users/sign_in",
      redirectDelayMilliseconds: 0,
      preserveUnauthorizedToastMessage: sessionExpiredMessage,
      preferFallbackPathOnRedirect: true,
    });
  }, []);

  const {
    exists: projectRequestExists,
    isActive: isProjectActive,
    isStatusLoading: isProjectStatusLoading,
    modalOpen: isProjectModalOpen,
    error: projectStatusError,
    handleChangeStatus: handleChangeProjectStatus,
    handleConfirmStop: handleConfirmProjectStop,
    handleCancelStop: handleCancelProjectStop,
  } = useRequestStatus({
    enabled: isSkillSheetBasicCompleted,
    fetchPath: "/user_contract_request/edit.json",
    togglePath: "/user_contract_request/toggle_recruiting",
    existsKey: "contract_request_exists",
    eventName: "contract:status-updated",
    requestParamKey: "user_contract_request",
    errorMessage: requestStatusErrorMessage,
    handleSessionExpired,
    successMessages: {
      on: "プロジェクト募集を開始しました。",
      off: "プロジェクト募集を停止しました。",
    },
  });

  const {
    exists: jobRequestExists,
    isActive: isJobActive,
    isStatusLoading: isJobStatusLoading,
    error: jobStatusError,
    modalOpen: isJobModalOpen,
    handleChangeStatus: handleChangeJobStatus,
    handleConfirmStop: handleConfirmJobStop,
    handleCancelStop: handleCancelJobStop,
  } = useRequestStatus({
    enabled: isSkillSheetBasicCompleted,
    fetchPath: "/user_job_request/edit.json",
    togglePath: "/user_job_request/toggle_recruiting",
    existsKey: "job_request_exists",
    eventName: "job:status-updated",
    requestParamKey: "user_job_request",
    errorMessage: requestStatusErrorMessage,
    handleSessionExpired,
    successMessages: {
      on: "転職先募集を開始しました。",
      off: "転職先募集を停止しました。",
    },
  });

  // メールアドレス変更
  const handleEmailSubmit = async () => {
    if (isSubmittingEmail) {
      return;
    }

    setIsSubmittingEmail(true);

    try {
      const response = await fetch("/users/update_email", {
        method: "PATCH",
        headers: {
          Accept: "application/json",
          "Content-Type": "application/json",
          "X-CSRF-Token": document
            .querySelector('meta[name="csrf-token"]')
            ?.getAttribute("content"),
        },
        body: JSON.stringify({ user: { email } }),
      });

      if (handleSessionExpired(response)) {
        return;
      }

      const contentType = response.headers.get("content-type") || "";
      const isJsonResponse = contentType.includes("application/json");
      const data = isJsonResponse ? await response.json() : null;

      if (response.ok) {
        toast.success(
          data?.message ??
            "新しいメールアドレス宛に確認メールを送信しました。メールをご確認ください。",
        );
        setEmail("");
      } else {
        const errorMessage = Array.isArray(data?.errors)
          ? data.errors.join("、")
          : (data?.message ?? "変更に失敗しました。");
        toast.error(errorMessage);
      }
    } catch (error) {
      toast.error("変更に失敗しました。時間をおいて再度お試しください。");
    } finally {
      setIsSubmittingEmail(false);
    }
  };

  const isBlank = (value) => {
    return typeof value !== "string" || value.trim().length === 0;
  };

  // パスワード変更
  const handlePasswordChange = async (event) => {
    event.preventDefault();

    if (isSubmittingPassword) {
      return;
    }

    if (!passwordChangeAllowed) {
      toast.error("このアカウントではパスワード変更は利用できません");
      return;
    }

    if (
      isBlank(currentPassword) ||
      isBlank(newPassword) ||
      isBlank(newPasswordConfirmation)
    ) {
      toast.error("未入力の項目があります");
      return;
    }

    if (
      typeof minimumPasswordLength === "number" &&
      newPassword.length < minimumPasswordLength
    ) {
      toast.error(
        `新しいパスワードは${minimumPasswordLength}文字以上で入力してください`,
      );
      return;
    }

    if (newPassword !== newPasswordConfirmation) {
      toast.error("新しいパスワードと再入力の内容が一致しません");
      return;
    }

    if (currentPassword === newPassword) {
      toast.error("現在のパスワードと同じパスワードは設定できません");
      return;
    }

    setIsSubmittingPassword(true);

    try {
      const response = await fetch("/settings/password", {
        method: "PATCH",
        headers: {
          Accept: "application/json",
          "Content-Type": "application/json",
          "X-CSRF-Token": document
            .querySelector('meta[name="csrf-token"]')
            ?.getAttribute("content"),
        },
        body: JSON.stringify({
          password: {
            current_password: currentPassword,
            new_password: newPassword,
            new_password_confirmation: newPasswordConfirmation,
          },
        }),
      });

      const wasHandled = handleSessionExpired(response);
      if (wasHandled) {
        return;
      }

      const contentType = response.headers.get("content-type") || "";
      const isJsonResponse = contentType.includes("application/json");
      const responseBody = isJsonResponse ? await response.json() : null;

      if (!response.ok) {
        toast.error(responseBody?.message || "パスワードの変更に失敗しました");
        return;
      }

      toast.success(responseBody?.message || "パスワードを変更しました");
      setCurrentPassword("");
      setNewPassword("");
      setNewPasswordConfirmation("");
    } catch (error) {
      toast.error("通信エラーが発生しました");
    } finally {
      setIsSubmittingPassword(false);
    }
  };

  const handleChangeMailMagazineStatus = async (category, label, checked) => {
    if (isSubmittingMailMagazine) return;

    const previousStatus = mailMagazineStatus;
    setMailMagazineStatus((prev) => ({ ...prev, [category]: checked }));
    setIsSubmittingMailMagazine(true);
    try {
      const response = await fetch("/api/users/mailmagazine_subscriptions", {
        method: "PATCH",
        headers: {
          Accept: "application/json",
          "Content-Type": "application/json",
          "X-CSRF-Token": document
            .querySelector('meta[name="csrf-token"]')
            ?.getAttribute("content"),
        },
        body: JSON.stringify({ user: { [category]: checked } }),
      });

      if (handleSessionExpired(response)) return;

      if (!response.ok) {
        setMailMagazineStatus(previousStatus);
        toast.error(`${label}の設定変更に失敗しました。`);
        return;
      }

      const data = await response.json();

      setMailMagazineStatus(pickMailMagazineStatus(data));

      toast.success(
        data[category]
          ? `${label}の配信を設定しました。`
          : `${label}の配信を停止しました。`,
      );
    } catch {
      setMailMagazineStatus(previousStatus);
      toast.error("通信エラーが発生しました。");
    } finally {
      setIsSubmittingMailMagazine(false);
    }
  };

  // 遷移先
  // プロジェクト　希望条件編集画面
  const handleClickProjectGuide = () => {
    const redirectPath = encodeURIComponent(window.location.pathname);
    navigate(`/user_contract_request/edit?redirect=${redirectPath}`);
  };

  // 求人　希望条件編集画面
  const handleClickJobGuide = () => {
    const redirectPath = encodeURIComponent(window.location.pathname);
    navigate(`/user_job_request/edit?redirect=${redirectPath}`);
  };

  // 退職画面
  const handleClickWithdrawal = () => {
    window.location.href = "/withdrawal";
  };

  // メールアドレスとminimum_password_lengthの取得
  useEffect(() => {
    const fetchUserSettings = async () => {
      try {
        const response = await fetch(`/user_settings.json`, {
          headers: {
            Accept: "application/json",
          },
        });

        if (handleSessionExpired(response)) {
          return;
        }

        if (!response.ok) {
          throw new Error("ユーザー情報の取得に失敗しました");
        }

        const contentType = response.headers.get("content-type") || "";
        if (!contentType.includes("application/json")) {
          throw new Error("ユーザー情報のレスポンス形式が不正です");
        }

        const json = await response.json();

        // マイページ公開状態
        setProfilePublicFlag(Boolean(json?.profile_public_flag));

        setDisplayedEmailAddress(
          json?.displayed_email_address || "メールアドレス未登録",
        );
        setEmailRegistered(json?.email_registered === true);
        setPasswordChangeAllowed(
          typeof json?.password_change_allowed === "boolean"
            ? json.password_change_allowed
            : null,
        );
        setMinimumPasswordLength(json?.minimum_password_length ?? null);
        setMailMagazineStatus(pickMailMagazineStatus(json));
      } catch (error) {
        console.error(error);
        toast.error("情報の取得に失敗しました");
      } finally {
        setIsLoading(false);
      }
    };

    fetchUserSettings();
  }, []);

  const sectionStyle = {
    borderBottom: `1px solid ${BORDER_COLOR_LIGHT}`,
    py: 4,
  };

  const sectionTitleStyle = {
    fontWeight: "bold",
    fontSize: isDesktop ? "1.2rem" : "1rem",
    whiteSpace: "nowrap",
    mb: 2,
  };

  const sectionContentStyle = {
    ml: isDesktop ? 2 : 0,
  };

  const textFieldStyle = {
    maxWidth: isDesktop ? "300px" : "100%",
  };

  if (isLoading) {
    return (
      <Box p={4} ml={4}>
        <Typography>読み込み中...</Typography>
      </Box>
    );
  }

  return (
    <FrameCard
      sx={{
        p: isDesktop ? 8 : 2,
        color: TEXT_COLOR_PRIMARY,
        "& .MuiTypography-root": {
          color: TEXT_COLOR_PRIMARY,
        },
        "& .MuiFormControlLabel-label": {
          color: TEXT_COLOR_PRIMARY,
        },
        "& .MuiInputBase-input": {
          color: TEXT_COLOR_PRIMARY,
        },
        "& .MuiOutlinedInput-notchedOutline": {
          borderColor: BORDER_COLOR_LIGHT,
        },
      }}
    >
      <Typography
        fontWeight="bold"
        sx={{
          fontSize: "1.8rem",
          whiteSpace: "nowrap",
        }}
      >
        設定
      </Typography>

      {/* メールアドレス */}
      <Box sx={sectionStyle}>
        <Typography sx={{ mb: 3 }}>{displayedEmailAddress}</Typography>
        <Typography sx={sectionTitleStyle}>メールアドレス</Typography>
        <Box sx={sectionContentStyle}>
          <Typography id="new-email-address-label">
            新しいメールアドレス
          </Typography>
          <TextField
            fullWidth
            sx={textFieldStyle}
            type="email"
            autoComplete="email"
            name="email"
            value={email}
            inputProps={{ "aria-labelledby": "new-email-address-label" }}
            onChange={(event) => setEmail(event.target.value)}
          />
          <ActionButton
            label={isSubmittingEmail ? "送信中..." : "確認メールを送信する"}
            value={email}
            onClick={handleEmailSubmit}
            disabled={isSubmittingEmail}
            sx={{
              mt: 4,
              color: "#fff",
              backgroundColor: SEND_COLOR,
              "&:hover": { backgroundColor: SEND_COLOR_DARK },
            }}
          />
        </Box>
      </Box>

      {/* パスワード */}
      <Box sx={sectionStyle}>
        <Typography sx={sectionTitleStyle}>パスワード</Typography>
        <Box sx={sectionContentStyle}>
          {passwordChangeAllowed === null ? (
            <>
              <Typography sx={{ mb: 2 }}>
                パスワード変更設定の取得に失敗しました。時間をおいて再度お試しください。
              </Typography>
              <Button
                variant="outlined"
                onClick={() => window.location.reload()}
              >
                再読み込み
              </Button>
            </>
          ) : passwordChangeAllowed ? (
            <>
              <Typography id="current-password-label">
                現在のパスワード
              </Typography>
              <TextField
                fullWidth
                type="password"
                autoComplete="current-password"
                sx={{ ...textFieldStyle, mb: 3 }}
                inputProps={{ "aria-labelledby": "current-password-label" }}
                value={currentPassword}
                onChange={(event) => setCurrentPassword(event.target.value)}
              />
              <Typography id="new-password-label">新しいパスワード</Typography>
              <Box>
                <TextField
                  fullWidth
                  type="password"
                  autoComplete="new-password"
                  sx={textFieldStyle}
                  inputProps={{ "aria-labelledby": "new-password-label" }}
                  value={newPassword}
                  onChange={(event) => setNewPassword(event.target.value)}
                />
                {typeof minimumPasswordLength === "number" && (
                  <Typography sx={{ fontSize: "12px", mb: 3 }}>
                    {`※パスワードは${minimumPasswordLength}文字以上で設定してください`}
                  </Typography>
                )}
              </Box>
              <Typography id="confirm-new-password-label">
                新しいパスワード再入力
              </Typography>
              <TextField
                fullWidth
                type="password"
                autoComplete="new-password"
                sx={{ ...textFieldStyle, mb: 3 }}
                inputProps={{ "aria-labelledby": "confirm-new-password-label" }}
                value={newPasswordConfirmation}
                onChange={(event) =>
                  setNewPasswordConfirmation(event.target.value)
                }
              />
              <ActionButton
                label={isSubmittingPassword ? "変更中..." : "変更"}
                onClick={handlePasswordChange}
                disabled={isSubmittingPassword}
                sx={{
                  mt: 4,
                  color: "#fff",
                  backgroundColor: SEND_COLOR,
                  "&:hover": { backgroundColor: SEND_COLOR_DARK },
                }}
              />
            </>
          ) : (
            <Typography>
              このアカウントは外部サービスでログインしているため、パスワード変更は利用できません。
            </Typography>
          )}
        </Box>
      </Box>

      {/* 希望条件公開設定 */}
      <Box sx={sectionStyle}>
        <Typography sx={sectionTitleStyle}>希望条件公開設定</Typography>
        {isSkillSheetBasicCompleted ? (
          <Box sx={sectionContentStyle}>
            <RequestStatusSwitch
              isProjectActive={isProjectActive}
              onToggleProject={handleChangeProjectStatus}
              projectRequestExists={projectRequestExists}
              isProjectStatusLoading={isProjectStatusLoading}
              projectStatusError={projectStatusError}
              projectStatusErrorMessage={requestStatusErrorMessage}
              isJobActive={isJobActive}
              onToggleJob={handleChangeJobStatus}
              jobRequestExists={jobRequestExists}
              isJobStatusLoading={isJobStatusLoading}
              jobStatusError={jobStatusError}
              jobStatusErrorMessage={requestStatusErrorMessage}
              onClickProjectGuide={handleClickProjectGuide}
              onClickJobGuide={handleClickJobGuide}
            />
          </Box>
        ) : (
          <Box
            sx={{
              ...sectionContentStyle,
              width: isDesktop ? 340 : "100%",
            }}
          >
            <NoticeBanner
              text={
                "基本情報を登録すると、\n公開設定を変更できるようになります。"
              }
            />
          </Box>
        )}
      </Box>

      {/* マイページ公開設定 */}
      <ProfileVisibilitySection
        profilePublicFlag={profilePublicFlag}
        sectionStyle={sectionStyle}
        sectionTitleStyle={sectionTitleStyle}
        sectionContentStyle={sectionContentStyle}
        handleSessionExpired={handleSessionExpired}
        isSkillSheetBasicCompleted={isSkillSheetBasicCompleted}
      />

      {/* メール設定 */}
      <MailSettingsSection
        emailRegistered={emailRegistered}
        isDesktop={isDesktop}
        sectionStyle={sectionStyle}
        sectionTitleStyle={sectionTitleStyle}
        sectionContentStyle={sectionContentStyle}
        mailMagazineOptions={MAIL_MAGAZINE_OPTIONS}
        mailMagazineStatus={mailMagazineStatus}
        isSubmittingMailMagazine={isSubmittingMailMagazine}
        handleChangeMailMagazineStatus={handleChangeMailMagazineStatus}
      />

      {/* 退会 */}
      <Box sx={{ my: 2 }}>
        <Typography sx={sectionTitleStyle}>退会</Typography>
        <Box sx={sectionContentStyle}>
          <Button variant="text" onClick={handleClickWithdrawal} color="error">
            退会する →
          </Button>
        </Box>
      </Box>
      <ConfirmStopModal
        open={isProjectModalOpen}
        onClose={handleCancelProjectStop}
        onConfirm={handleConfirmProjectStop}
        title={
          isProjectActive ? "プロジェクト募集を停止" : "プロジェクト募集を開始"
        }
        message={
          isProjectActive
            ? "プロジェクト募集を停止にするとプロジェクト募集に関する案内を受け取れなくなります。"
            : "プロジェクト募集を開始すると運営局からの連絡やプロジェクト紹介を受け取れます。"
        }
        confirmLabel={
          isProjectActive
            ? "プロジェクト募集を停止する"
            : "プロジェクト募集を開始する"
        }
      />

      <ConfirmStopModal
        open={isJobModalOpen}
        onClose={handleCancelJobStop}
        onConfirm={handleConfirmJobStop}
        title={isJobActive ? "転職先募集を停止" : "転職先募集を開始"}
        message={
          isJobActive
            ? "転職先募集を停止にすると転職先募集に関する案内を受け取れなくなります。"
            : "転職先募集を開始すると運営局からの連絡や転職先募集に関する案内を受け取れます。"
        }
        confirmLabel={
          isJobActive ? "転職先募集を停止する" : "転職先募集を開始する"
        }
      />
    </FrameCard>
  );
}
